/**
 * MyToolsSection — the "My Tools" surface on /my-matches.
 *
 * Shows EVERY consumer-facing product in the catalog (tools + bundle +
 * VIP) split into two sections:
 *   1. Tools — the 4 individual tools + Buyer Toolkit bundle
 *   2. VIP   — VIP Buying Plan + VIP Concierge
 *
 * Dealer-only products (dealer_founding_monthly, dealer_setup) are
 * intentionally excluded — they belong on the /dealer-plans page.
 *
 * Purchased products show an "Open tool" (or "Included") CTA. Unpurchased
 * products show a "Get for $X.XX" CTA that jumps to /checkout/{product_id}.
 *
 * Includes a "Receipts & billing" button in the footer that opens the
 * Stripe Customer Portal (silent if the buyer hasn't purchased anything
 * yet — Stripe has no customer for them).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShoppingBag, ArrowRight, CheckCircle2, Receipt, Crown, Sparkles,
} from 'lucide-react';
import { PRODUCTS } from '../../data/products';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONSUMER_PRODUCTS = PRODUCTS.filter(
  (p) => p.category !== 'dealer' && !p.hiddenFromCatalog,
);
const TOOL_PRODUCTS = CONSUMER_PRODUCTS.filter(p => p.category === 'tool' || p.category === 'bundle');
const VIP_PRODUCTS  = CONSUMER_PRODUCTS.filter(p => p.category === 'vip');

const MyToolsSection = ({ email, filter = 'all', sessionId }) => {
  const [purchases, setPurchases] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  useEffect(() => {
    const target = (email || '').trim().toLowerCase();
    if (!target || !target.includes('@')) { setLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/my/purchases`, { params: { email: target } });
        if (!cancelled) setPurchases(res.data?.products || []);
      } catch {
        if (!cancelled) setPurchases([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [email]);

  const ownedIds = useMemo(
    () => new Set(purchases.map(p => p.product_id).filter(Boolean)),
    [purchases],
  );

  const openPortal = async () => {
    setPortalError('');
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/payments/portal`, {
        email: (email || '').trim().toLowerCase(),
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setPortalError('Billing portal is not available yet.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Billing portal is not available yet.';
      setPortalError(detail);
    } finally {
      setPortalLoading(false);
    }
  };

  if (!loaded) return null;

  const ownsAny = ownedIds.size > 0;

  return (
    <section
      className="mt-6 space-y-6"
      data-testid="my-tools-section"
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <ShoppingBag className="w-5 h-5 text-amber-300" />
        <h2 className="text-lg font-black uppercase tracking-widest text-white">My Tools</h2>
        <span className="text-xs text-slate-400 ml-2">
          {ownsAny ? `You own ${ownedIds.size} of ${CONSUMER_PRODUCTS.length}` : 'Unlock any tool below to see it here'}
        </span>
      </div>

      {/* Tools group */}
      {(filter === 'all' || filter === 'tools') && (
        <ProductGroup
          title="Tools"
          icon={ShoppingBag}
          products={TOOL_PRODUCTS}
          ownedIds={ownedIds}
          email={email}
          sessionId={sessionId}
          testid="tools-group"
        />
      )}

      {/* VIP group — Buying Plan + Concierge */}
      {(filter === 'all' || filter === 'vip') && (
        <ProductGroup
          title="VIP"
          icon={Crown}
          subtitle="One-on-one help from an RV Match advisor when you want a real human in your corner."
          products={VIP_PRODUCTS}
          ownedIds={ownedIds}
          email={email}
          sessionId={sessionId}
          accent="fuchsia"
          testid="vip-group"
        />
      )}

      {/* Receipts + billing self-serve — Stripe Customer Portal. */}
      <div className="flex items-center justify-end gap-3 text-xs" data-testid="billing-portal-row">
        {portalError && (
          <span className="text-amber-300/90" data-testid="billing-portal-error">{portalError}</span>
        )}
        <button
          type="button"
          onClick={openPortal}
          disabled={portalLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.03] text-slate-200 hover:bg-white/[0.08] hover:text-white disabled:opacity-60"
          data-testid="my-tools-open-billing-portal"
        >
          <Receipt className="w-3.5 h-3.5" />
          {portalLoading ? 'Opening…' : 'Receipts & billing'}
        </button>
      </div>
    </section>
  );
};

const ProductGroup = ({ title, icon: Icon, subtitle, products, ownedIds, email, sessionId, accent = 'amber', testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${accent === 'fuchsia' ? 'text-fuchsia-300' : 'text-amber-300'}`} />
      <h3 className={`text-sm font-black uppercase tracking-widest ${accent === 'fuchsia' ? 'text-fuchsia-200' : 'text-amber-200'}`}>
        {title}
      </h3>
    </div>
    {subtitle && <p className="text-xs text-slate-400 mb-3 max-w-xl">{subtitle}</p>}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
      {products.map(p => (
        <ProductTile key={p.id} product={p} owned={ownedIds.has(p.id)} accent={accent} email={email} sessionId={sessionId} />
      ))}
    </div>
  </div>
);

const ProductTile = ({ product, owned, accent, email, sessionId }) => {
  const isVip = product.category === 'vip';
  const isBundle = product.category === 'bundle';
  const borderClass = owned
    ? 'border-emerald-500/40'
    : isVip
      ? 'border-fuchsia-500/25'
      : isBundle
        ? 'border-amber-500/25'
        : 'border-white/10';
  const gradient = isVip
    ? 'from-fuchsia-500/[0.08] via-violet-500/[0.05] to-transparent'
    : isBundle
      ? 'from-amber-500/[0.08] via-orange-500/[0.05] to-transparent'
      : 'from-white/[0.02] to-transparent';

  return (
    <div
      className={`rounded-2xl overflow-hidden border ${borderClass} bg-gradient-to-br ${gradient} flex flex-col`}
      data-testid={`product-tile-${product.id}`}
    >
      {product.image && (
        <div className="aspect-[16/10] w-full bg-[#05070d] overflow-hidden border-b border-white/5">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-contain"
          />
        </div>
      )}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-black text-white leading-tight">{product.name}</h4>
          {owned && (
            <span
              className="shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-400/30"
              data-testid={`product-owned-badge-${product.id}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Yours
            </span>
          )}
        </div>
        <p className="text-xs text-slate-300 leading-snug line-clamp-2">{product.tagline}</p>

        <div className="mt-auto pt-2 flex items-center justify-between gap-2">
          <div>
            <div className="text-lg font-black text-white leading-none">{product.priceLabel}</div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {product.payment === 'recurring-yearly' ? 'Per year' : 'One-time'}
            </div>
          </div>
          {owned ? (
            <Link
              to={`/my-tool/${product.id}?${new URLSearchParams({ ...(email ? { email } : {}), ...(sessionId ? { sessionId } : {}) }).toString()}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-black"
              data-testid={`product-open-btn-${product.id}`}
            >
              Open <ArrowRight className="w-3 h-3" />
            </Link>
          ) : (
            <Link
              to={product.href}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-black ${
                isVip
                  ? 'bg-fuchsia-500 hover:bg-fuchsia-400 text-white'
                  : isBundle
                    ? 'bg-amber-400 hover:bg-amber-300 text-black'
                    : 'bg-white hover:bg-slate-100 text-[#05070d]'
              }`}
              data-testid={`product-buy-btn-${product.id}`}
            >
              <Sparkles className="w-3 h-3" /> Get it
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyToolsSection;
